import { assert, type NullableValues, type ReadonlyRecord } from '../utils'

import { ConnectwareError, ConnectwareErrorType, type PaginationParameters } from '.'

export type CustomizedTable<Line extends ReadonlyRecord<string, unknown>> =
    & NullableValues<Pick<PaginationParameters<Line>, 'search' | 'sort'>>
    & PaginationParameters<Line>['pagination']

/**
 * How a user arranged a table's columns. It's keyed by column name, so it can come from storage that outlived a column:
 * names that are no longer columns are ignored, and every change drops them
 */
export type ColumnView = Readonly<{
    order: readonly string[]
    hidden: readonly string[]
    pinned: readonly string[]
    /** In pixels, only for the columns the user sized */
    widths: Readonly<Record<string, number>>
}>

export const defaultColumnView: ColumnView = { order: [], hidden: [], pinned: [], widths: {} }

const onlyColumns = <Name extends string>(names: readonly Name[], saved: readonly string[]): readonly Name[] => names.filter((name) => saved.includes(name))

/** The columns in the user's order, before pinning: columns the saved order lacks keep their default place */
const baseOrder = <Name extends string>(names: readonly Name[], { order }: ColumnView): readonly Name[] => {
    const ordered: Name[] = onlyColumns(names, order)
        .slice()
        .sort((a, b) => order.indexOf(a) - order.indexOf(b))

    names.forEach((name, defaultIndex) => {
        if (!order.includes(name)) {
            ordered.splice(Math.min(defaultIndex, ordered.length), 0, name)
        }
    })

    return ordered
}

const isSameOrder = (a: readonly string[], b: readonly string[]): boolean => a.length === b.length && a.every((name, index) => name === b[index])

const isValidWidth = (width: number): boolean => Number.isFinite(width) && width > 0

const onlyColumnWidths = <Name extends string>(names: readonly Name[], widths: ColumnView['widths']): ColumnView['widths'] =>
    names.reduce<ColumnView['widths']>((r, name) => {
        const width = widths[name]
        return width !== undefined && isValidWidth(width) ? { ...r, [name]: width } : r
    }, {})

/** Drops names that aren't columns, duplicates and invalid widths, and stores no order when it's the default one */
const prune = <Name extends string>(names: readonly Name[], view: ColumnView): ColumnView => {
    const order = baseOrder(names, view)

    return {
        order: isSameOrder(order, names) ? [] : order,
        hidden: onlyColumns(names, view.hidden),
        pinned: onlyColumns(names, view.pinned),
        widths: onlyColumnWidths(names, view.widths),
    }
}

export const isDefaultColumnView = <Name extends string>(names: readonly Name[], view: ColumnView): boolean => {
    const { order, hidden, pinned, widths } = prune(names, view)
    return order.length === 0 && hidden.length === 0 && pinned.length === 0 && Object.keys(widths).length === 0
}

/** Every column in display order, pinned ones first */
export const orderColumns = <Name extends string>(names: readonly Name[], view: ColumnView): readonly Name[] => {
    const ordered = baseOrder(names, view)
    return [...ordered.filter((name) => view.pinned.includes(name)), ...ordered.filter((name) => !view.pinned.includes(name))]
}

/** The columns to render, in display order. A table never shows none: with everything hidden, the first column stays */
export const applyColumnView = <Name extends string>(names: readonly Name[], view: ColumnView): readonly Name[] => {
    const ordered = orderColumns(names, view)
    const visible = ordered.filter((name) => !view.hidden.includes(name))
    const [first] = ordered

    return visible.length > 0 || first === undefined ? visible : [first]
}

/** The last visible column can't be hidden */
export const toggleColumnHidden = <Name extends string>(names: readonly Name[], view: ColumnView, name: Name): ColumnView => {
    if (!names.includes(name)) {
        return prune(names, view)
    }

    if (view.hidden.includes(name)) {
        return prune(names, { ...view, hidden: view.hidden.filter((hidden) => hidden !== name) })
    }

    return prune(names, applyColumnView(names, view).length > 1 ? { ...view, hidden: [...view.hidden, name] } : view)
}

export const toggleColumnPinned = <Name extends string>(names: readonly Name[], view: ColumnView, name: Name): ColumnView => {
    if (!names.includes(name)) {
        return prune(names, view)
    }

    return prune(names, { ...view, pinned: view.pinned.includes(name) ? view.pinned.filter((pinned) => pinned !== name) : [...view.pinned, name] })
}

/**
 * Moves a column to the place of another, as drag and drop reports it (the dragged column and the one it's dropped on).
 * Pinned and unpinned columns are ordered separately, so the target should be in the same group:
 * dropped on the other group, the column keeps its pin and lands next to the target in the underlying order
 */
export const moveColumn = <Name extends string>(names: readonly Name[], view: ColumnView, name: Name, target: Name): ColumnView => {
    const ordered = baseOrder(names, view).slice()
    const from = ordered.indexOf(name)
    const to = ordered.indexOf(target)

    if (from === -1 || to === -1) {
        return prune(names, view)
    }

    ordered.splice(from, 1)
    ordered.splice(to, 0, name)

    return prune(names, { ...view, order: ordered })
}

/** Sizes a column in whole pixels, never below the given minimum. A width that isn't a finite number changes nothing */
export const setColumnWidth = <Name extends string>(names: readonly Name[], view: ColumnView, name: Name, width: number, minWidth: number): ColumnView => {
    assert(
        Number.isFinite(minWidth) && minWidth > 0,
        new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'A column minimum width must be a positive number', { minWidth })
    )

    if (!names.includes(name) || !Number.isFinite(width)) {
        return prune(names, view)
    }

    return prune(names, { ...view, widths: { ...view.widths, [name]: Math.max(Math.round(width), Math.ceil(minWidth)) } })
}

/** The valid widths the user set for the given columns */
export const columnWidths = <Name extends string>(names: readonly Name[], view: ColumnView): ColumnView['widths'] => onlyColumnWidths(names, view.widths)
